import React, { useState, useEffect, useRef, ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';

import { motion } from 'framer-motion';
import { ChevronRight, ChevronLeft, FigmaIcon, ShoppingBag, ShoppingBasket, LucideIcon } from 'lucide-react';

interface Platform {
  name: string;
  previewUrl: string;
  detailsUrl: string;
}

interface Theme {
  id: number;
  name: string;
  description: string;
  featuredImage: {
    src: string;
    alt: string;
  };
  platforms: Platform[];
}

interface PlatformButtonProps {
  platform: Platform;
  previewUrl: string;
  detailsUrl: string;
  isActive: boolean;
  onHover: (platform: Platform) => void;
}

const PlatformButton: React.FC<PlatformButtonProps> = ({ platform, previewUrl, detailsUrl, isActive, onHover }) => {
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const icons: { [key: string]: LucideIcon } = {
    figma: FigmaIcon,
    shopify: ShoppingBag,
    spree: ShoppingBasket,
  };
  const Icon = icons[platform.name];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node) &&
        popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setIsPopupVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMouseEnter = () => {
    setIsPopupVisible(true);
    onHover(platform);
  };

  return (
    <div className="relative" ref={buttonRef}>
      <div
        className="p-4 text-[#0474e0] transition-colors rounded-full bg-white/90 hover:bg-white cursor-pointer"
        onMouseEnter={handleMouseEnter}
        onClick={() => setIsPopupVisible(!isPopupVisible)}
      >
        <Icon size={32} />
      </div>
      {isPopupVisible && isActive && (
        <div
          ref={popupRef}
          className="absolute flex p-2 transform -translate-x-1/2 bg-white rounded-lg shadow-lg -top-16 left-1/2"
          style={{ minWidth: '240px' }}
        >
          <div className="absolute left-0 w-full h-4 mb-2 bg-transparent -top-4" />
          <button
            onClick={() => window.open(previewUrl, '_blank')}
            className="flex-1 px-3 py-2 text-sm font-semibold text-white bg-[#0474e0] rounded-md transition-transform hover:scale-105 mr-2"
          >
            Preview
          </button>
          <button
            onClick={() => window.open(detailsUrl, '_blank')}
            className="flex-1 px-3 py-2 text-sm font-semibold text-[#0474e0] bg-white rounded-md border border-[#0474e0] transition-transform hover:scale-105"
          >
            Buy
          </button>
        </div>
      )}
    </div>
  );
};

interface ThemeCardProps {
  theme: Theme;
  isActive: boolean;
  distance: number;
  direction: number;
  isTransitioning: boolean;
}

const ThemeCard: React.FC<ThemeCardProps> = ({ theme, isActive, distance, direction, isTransitioning }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [activePlatform, setActivePlatform] = useState<Platform | null>(null);

  const scale = isActive ? 1 : 1 - Math.abs(distance) * 0.2;
  const translateX = -distance * 234 * direction;
  const zIndex = isActive ? 10 : 5 - Math.abs(distance);

  const isEdgeCard = Math.abs(distance) === 2;

  const gradientStyle = isActive
    ? 'from-[#0474e0]/80 via-[#0474e0]/30 to-transparent'
    : 'from-[#fcebef]/60 via-[#fcebef]/30 to-transparent';

  return (
    <div
      className={`absolute bottom-0 left-1/2 rounded-lg overflow-hidden transition-all duration-500 ease-in-out ${
        isActive ? 'shadow-2xl' : 'shadow-lg'
      }`}
      style={{
        transform: `translateX(calc(${translateX}px - 50%)) scale(${scale})`,
        width: isActive ? 499 : 416,
        height: isActive ? 416 : 333,
        zIndex,
        visibility: Math.abs(distance) > 2 ? 'hidden' : 'visible',
        transition: isEdgeCard ? 'transform 500ms ease-in-out' : 'all 500ms ease-in-out',
      }}
    >
      <div className="relative w-full h-full group">
        {!imageLoaded && (
          <Skeleton
            className="absolute inset-0"
            style={{ height: '100%', width: '100%' }}
          />
        )}
        <img
          src={theme.featuredImage.src}
          alt={theme.featuredImage.alt}
          className={`object-cover w-full h-full ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
        />
        <div className={`absolute inset-0 bg-gradient-to-t ${gradientStyle}`} />

        {isActive && (
          <>
            <div className="absolute transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2">
              <div className="flex space-x-8">
                {theme.platforms.map((platform) => (
                  <PlatformButton
                    key={platform.name}
                    platform={platform}
                    previewUrl={platform.previewUrl}
                    detailsUrl={platform.detailsUrl}
                    isActive={platform === activePlatform}
                    onHover={setActivePlatform}
                  />
                ))}
              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h3 className="mb-2 text-2xl font-bold text-white">
                {imageLoaded ? theme.name : <Skeleton width={150} />}
              </h3>
              <p className="text-sm text-white/90 line-clamp-2">
                {imageLoaded ? theme.description : <Skeleton count={2} />}
              </p>
            </div>
          </>
        )}

        {!isActive && (
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="text-lg font-semibold text-gray-800 opacity-80">
              {imageLoaded ? theme.name : <Skeleton width={100} />}
            </h3>
          </div>
        )}
      </div>
    </div>
  );
};

interface AnimatedTextProps {
  children: ReactNode;
  delay?: number;
}

const AnimatedText: React.FC<AnimatedTextProps> = ({ children, delay = 0 }) => {
  return (
    <motion.span
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        type: "spring",
        damping: 12,
        stiffness: 100,
        delay
      }}
    >
      {children}
    </motion.span>
  );
};

interface TextHighlightProps {
  children: ReactNode;
}

const TextHighlight: React.FC<TextHighlightProps> = ({ children }) => {
  return (
    <motion.span
      className="text-primary-500"
      whileHover={{
        scale: 1.05,
        color: "#0366d6",
        transition: { duration: 0.2 }
      }}
    >
      {children}
    </motion.span>
  );
};

const HeroText: React.FC = () => {
  return (
    <div className="mb-6 text-center">
      <motion.h1
        className="mb-2 text-3xl font-bold text-black md:text-4xl"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: "spring",
          damping: 10,
          stiffness: 100,
        }}
      >
        <AnimatedText delay={0.1}><TextHighlight>Niche-Perfected</TextHighlight></AnimatedText>{" "}
        <AnimatedText delay={0.2}>E-commerce Themes</AnimatedText>
      </motion.h1>
      <motion.p
        className="max-w-2xl mx-auto text-base text-gray-700"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: "spring",
          damping: 12,
          stiffness: 100,
          delay: 0.3,
        }}
      >
        Battle-tested designs for your industry's unique challenges
      </motion.p>
    </div>
  );
};

interface ThemeSliderProps {
  themes: Theme[];
}

const ThemeSlider: React.FC<ThemeSliderProps> = ({ themes }) => {
  const [activeIndex, setActiveIndex] = useState(2);
  const [direction, setDirection] = useState(1);
  const [autoScrollDirection, setAutoScrollDirection] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const totalThemes = themes.length;
  const autoScrollTimerRef = useRef<NodeJS.Timeout | null>(null);

  const getVisibleThemes = () => {
    const visibleThemes = [];
    for (let i = -2; i <= 2; i++) {
      const index = (activeIndex + i * direction + totalThemes) % totalThemes;
      visibleThemes.push({
        ...themes[index],
        distance: i,
        isTransitioning: isTransitioning && (i === -2 || i === 2)
      });
    }
    return visibleThemes;
  };

  const handleNext = () => {
    setDirection(1);
    setAutoScrollDirection(1);
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % totalThemes);
      setIsTransitioning(false);
    }, 250);
    resetAutoScrollTimer();
  };

  const handlePrev = () => {
    setDirection(-1);
    setAutoScrollDirection(-1);
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveIndex((prevIndex) => (prevIndex - 1 + totalThemes) % totalThemes);
      setIsTransitioning(false);
    }, 250);
    resetAutoScrollTimer();
  };

  const autoScroll = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveIndex((prevIndex) => (prevIndex + autoScrollDirection + totalThemes) % totalThemes);
      setIsTransitioning(false);
    }, 250);
  };

  const resetAutoScrollTimer = () => {
    if (autoScrollTimerRef.current) {
      clearInterval(autoScrollTimerRef.current);
    }
    autoScrollTimerRef.current = setInterval(autoScroll, 3000);
  };

  useEffect(() => {
    resetAutoScrollTimer();
    return () => {
      if (autoScrollTimerRef.current) {
        clearInterval(autoScrollTimerRef.current);
      }
    };
  }, [autoScrollDirection]);

  return (
    <div className="w-full max-w-6xl px-4 py-4 mx-auto">
      <div className="pink-blur"></div>
      <div className="blue-blur"></div>
      <HeroText />
      <div className="relative overflow-hidden h-[450px]">
        <div className="absolute inset-0">
          {getVisibleThemes().map((theme) => (
            <ThemeCard
              key={theme.id}
              theme={theme}
              isActive={theme.distance === 0}
              distance={theme.distance}
              direction={direction}
              isTransitioning={theme.isTransitioning}
            />
          ))}
        </div>
        <button 
          className="absolute left-0 z-10 p-3 transition-all duration-300 transform -translate-y-1/2 bg-white rounded-full shadow-md swiper-button-prev top-1/2 hover:bg-primary-100"
          onClick={handlePrev}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 19L8 12L15 5" stroke="#0474e0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button 
          className="absolute right-0 z-10 p-3 transition-all duration-300 transform -translate-y-1/2 bg-white rounded-full shadow-md swiper-button-next top-1/2 hover:bg-primary-100"
          onClick={handleNext}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 5L16 12L9 19" stroke="#0474e0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ThemeSlider;
